/**
 * The analysis catalogue, one audit's analyses, and the results they produce.
 */
import {
	queryOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';
import * as analysisApi from '../api/Analysis';
import { followPresigned, followPresignedPost } from '../api/download';
import { parsed } from '../api/parse';
import {
	analysisAvailabilityResponseSchema,
	analysisDefinitionSchema,
	analysisDetailResponseSchema,
	analysisOverviewSchema,
	lineageResultSchema,
	resultPageSchema,
	type CanonicalRowFilter,
	type ResultPage,
} from '../schemas';
import { keys } from './keys';
import type { ModuleCode } from '../features/modules/modules';
import { useAppDispatch } from '../store';
import { watchAudit } from '../store/runWatchSlice';

export function useAnalysisCatalogueQuery(module?: ModuleCode) {
	return useQuery({
		queryKey: keys.analyses.catalogue(module),
		queryFn: async () =>
			parsed(
				z.array(analysisDefinitionSchema),
				await analysisApi.getAnalysisCatalogue(module),
				'/analyses',
			),
		/* The catalogue is a static definition list on the server; it does not
       change while somebody is filling in a form. */
		staleTime: Infinity,
	});
}

/** Where one result row came from. */
export function useLineageQuery(path: string | null) {
	return useQuery({
		queryKey: ['lineage', path ?? ''] as const,
		queryFn: async () =>
			parsed(
				lineageResultSchema,
				await analysisApi.getLineage(path as string),
				path as string,
			),
		enabled: Boolean(path),
	});
}

/** Whether anything in this set of rows is still moving — the one condition
 *  every poller in this module re-checks on. */
function stillMoving(rows: Array<{ status: string }>): boolean {
	return rows.some(
		(row) => row.status === 'QUEUED' || row.status === 'RUNNING',
	);
}

/** The audit's analyses, one row per AuditAnalysis. Re-read every few seconds
 *  while anything is queued or running; stops the moment everything settles. */
export function useAnalysisOverviewQuery(auditId: string) {
	return useQuery(analysisOverviewQueryOptions(auditId));
}

/** The overview query itself, shared with `AnalysisRunWatcher`, which reads
 *  the same data (same cache entry) for every audit with a run in progress. */
export function analysisOverviewQueryOptions(auditId: string) {
	return queryOptions({
		queryKey: keys.audits.analyses(auditId),
		queryFn: async () =>
			parsed(
				analysisOverviewSchema,
				await analysisApi.getAnalysisOverview(auditId),
				`/audits/${auditId}/analysis-overview`,
			),
		refetchInterval: (query) =>
			query.state.data && stillMoving(query.state.data.rows)
				? 3000
				: false,
	});
}

/** One analysis's own page: the row, its note, and (admin only) technical
 *  details. Polls while this one analysis is queued or running. */
export function useAnalysisDetailQuery(auditId: string, slug: string) {
	return useQuery({
		queryKey: keys.audits.analysisDetail(auditId, slug),
		queryFn: async () =>
			parsed(
				analysisDetailResponseSchema,
				await analysisApi.getAnalysisDetail(auditId, slug),
				`/audits/${auditId}/analyses/${slug}`,
			),
		enabled: Boolean(auditId) && Boolean(slug),
		refetchInterval: (query) =>
			query.state.data && stillMoving([query.state.data.analysis])
				? 3000
				: false,
	});
}

/** The wizard's "Choose analyses" step — Ready/Blocked for the whole
 *  catalogue, for one client and data-scope filter. */
export function useAnalysisAvailabilityQuery(
	clientId: string | null,
	module: ModuleCode,
	filter: CanonicalRowFilter,
	enabled = true,
) {
	const filterKey = JSON.stringify(filter);
	return useQuery({
		queryKey: keys.analyses.availability(clientId ?? '', module, filterKey),
		queryFn: async () =>
			parsed(
				analysisAvailabilityResponseSchema,
				await analysisApi.getAnalysisAvailability(
					clientId as string,
					module,
					filter,
				),
				'/audits/analysis-availability',
			),
		enabled: Boolean(clientId) && enabled,
	});
}

/** "Edit analyses" — Ready/Blocked for the whole catalogue against an
 *  existing audit's frozen scope. */
export function useAuditAnalysisAvailabilityQuery(
	auditId: string,
	enabled = true,
) {
	return useQuery({
		queryKey: keys.audits.analysisAvailability(auditId),
		queryFn: async () =>
			parsed(
				analysisAvailabilityResponseSchema,
				await analysisApi.getAuditAnalysisAvailability(auditId),
				`/audits/${auditId}/analysis-availability`,
			),
		enabled: Boolean(auditId) && enabled,
	});
}

function useAnalysesVerb<T>(
	auditId: string,
	call: (input: T) => Promise<unknown>,
) {
	const client = useQueryClient();
	const dispatch = useAppDispatch();
	return useMutation({
		mutationFn: call,
		onSuccess: () => {
			/* Tell the user as each analysis starts and finishes, wherever they go. */
			dispatch(watchAudit({ auditId, announceStart: true }));
			void client.invalidateQueries({
				queryKey: keys.audits.analyses(auditId),
			});
			void client.invalidateQueries({
				queryKey: keys.audits.detail(auditId),
			});
			void client.invalidateQueries({
				queryKey: ['audits', auditId, 'analyses'],
			});
			void client.invalidateQueries({
				queryKey: ['audits', auditId, 'result'],
			});
		},
	});
}

/** Run every chosen analysis, or (with slugs) just those. */
export const useRunAnalysesMutation = (auditId: string) =>
	useAnalysesVerb(auditId, (slugs: string[] | undefined) =>
		analysisApi.runAnalyses(auditId, slugs),
	);

export const useRetryFailedMutation = (auditId: string) =>
	useAnalysesVerb<void>(auditId, () =>
		analysisApi.retryFailedAnalyses(auditId),
	);

/** One page of one analysis's result. */
export function useAnalysisResultQuery(
	auditId: string,
	slug: string,
	query: analysisApi.ResultQuery,
	enabled: boolean,
) {
	return useQuery({
		queryKey: keys.audits.result(auditId, slug, JSON.stringify(query)),
		queryFn: async () =>
			parsed(
				resultPageSchema,
				await analysisApi.getAnalysisResult(auditId, slug, query),
				`/audits/${auditId}/analyses/${slug}/result`,
			),
		enabled,
		placeholderData: (previous) => previous,
	});
}

/** Every row matching a search, for a filtered download (stored rows only). */
export async function fetchMatchingRows(
	auditId: string,
	slug: string,
	query: Omit<analysisApi.ResultQuery, 'page' | 'pageSize'>,
) {
	const rows: ResultPage['rows'] = [];
	let columns: ResultPage['columns'] = [];
	for (let page = 1; ; page += 1) {
		const result = parsed(
			resultPageSchema,
			await analysisApi.getAnalysisResult(auditId, slug, {
				...query,
				page,
				pageSize: 5000,
			}),
			`/audits/${auditId}/analyses/${slug}/result`,
		);
		columns = result.columns;
		rows.push(...result.rows);
		if (rows.length >= result.total || result.rows.length === 0) break;
	}
	return { columns, rows };
}

/** One analysis's Excel workbook, under a business filename. */
export function downloadAnalysisExcel(auditId: string, slug: string) {
	return followPresigned(`/audits/${auditId}/analyses/${slug}/export`);
}

/** One client analysis's Excel: every report's result in one workbook. */
export function downloadClientAnalysisExcel(
	auditId: string,
	analysisNumber: number,
) {
	return followPresigned(
		`/audits/${auditId}/analysis/${analysisNumber}/export`,
	);
}

export type AnalysisExportPart =
	'result' | 'exceptions' | 'supporting' | 'summary';

export interface AnalysisExportSelection {
	reports: Array<{
		index: number;
		parts: AnalysisExportPart[];
		columns?: { result?: string[]; supporting?: string[] };
	}>;
}

/** One analysis's workbook with only the chosen parts and columns. */
export function exportClientAnalysisSelection(
	auditId: string,
	analysisNumber: number,
	selection: AnalysisExportSelection,
) {
	return followPresignedPost(
		`/audits/${auditId}/analysis/${analysisNumber}/export`,
		selection,
	);
}

/** Every result in the audit, as one ZIP of Excel workbooks. */
export function useExportPackMutation(auditId: string) {
	return useMutation({
		mutationFn: () => followPresigned(`/audits/${auditId}/analyses/export`),
	});
}
